// Account rules shared by the sign-up form (src/) and the Pages Functions (functions/), so what the
// browser accepts is exactly what the server accepts. Pure JS, no imports (same idea as similar.js).
//
// IMPORTANT: usernameKey() and emailKey() must stay in sync with the generated columns
// `username_key` / `email_key` in schema.sql and migrations/0006_account_keys.sql.
// Those columns are what the database enforces uniqueness on.

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 30;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;
export const EMAIL_MAX = 254;
export const NAME_MAX = 80;
export const COUNTRY_MIN = 2;
export const COUNTRY_MAX = 60;

const lowerAscii = (s) => s.replace(/[A-Z]/g, (c) => c.toLowerCase());
const trimSpaces = (s) => s.replace(/^ +| +$/g, ""); // only spaces, exactly like SQLite's trim()

/* ---------- identity keys: two accounts may never share a key ---------- */

// "Bob", "bob" and " BOB " are the same person's username.
export const usernameKey = (username) => lowerAscii(trimSpaces(String(username ?? "")));

// Case-insensitive, and anything after a "+" in the local part is ignored, so
// "Me@Mail.com" and "me+spam@mail.com" are the same mailbox.
export function emailKey(email) {
  const s = lowerAscii(trimSpaces(String(email ?? "")));
  const at = s.indexOf("@");
  const plus = s.indexOf("+");
  return at !== -1 && plus !== -1 && plus < at ? s.slice(0, plus) + s.slice(at) : s;
}

/* ---------- usernames ---------- */

// Names that could be mistaken for staff or for the system. Punctuation is ignored when comparing,
// so "ad.min" and "open_wrecks" are caught too.
const RESERVED = new Set([
  "deleted", "admin", "administrator", "root", "system", "sysadmin", "support", "help", "helpdesk",
  "moderator", "mod", "staff", "owner", "openwrecks", "anonymous", "anon", "null", "undefined",
  "unknown", "nobody", "everyone", "postmaster", "webmaster", "security", "abuse", "noreply",
  "official", "team",
]);
export const isReservedUsername = (username) => RESERVED.has(usernameKey(username).replace(/[._-]/g, ""));

// 3-30 characters: letters, numbers, dot, dash, underscore. Starts and ends with a letter or number.
// ASCII only on purpose: it rules out look-alike letters, zero-width characters and invisible spaces.
const USERNAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*[A-Za-z0-9]$/;

// Returns { value } (trimmed, original capitalisation kept for display) or { error }.
export function validateUsername(raw) {
  if (typeof raw !== "string") return { error: "Username is required" };
  const value = raw.trim();
  if (!value) return { error: "Username is required" };
  if (value.length < USERNAME_MIN || value.length > USERNAME_MAX) {
    return { error: `Username must be ${USERNAME_MIN}-${USERNAME_MAX} characters` };
  }
  if (!USERNAME_RE.test(value) || /[._-]{2}/.test(value)) {
    return { error: "Username can only use letters, numbers, dots, dashes and underscores (no spaces, and no two symbols in a row)" };
  }
  if (isReservedUsername(value)) return { error: "That username is reserved" };
  return { value };
}

/* ---------- email ---------- */

const EMAIL_RE =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

// Returns { value } (trimmed + lower-cased) or { error }.
export function validateEmail(raw) {
  if (typeof raw !== "string") return { error: "Email is required" };
  const value = raw.trim().toLowerCase();
  const local = value.split("@")[0];
  if (
    !value || value.length > EMAIL_MAX || local.length > 64 ||
    !EMAIL_RE.test(value) || local.startsWith(".") || local.endsWith(".") || value.includes("..")
  ) {
    return { error: "Please enter a valid email" };
  }
  return { value };
}

/* ---------- password ---------- */

// Passwords that appear at the top of every leaked-password list. (Not a substitute for a breach check.)
const COMMON_PASSWORDS = new Set([
  "password", "password1", "password12", "password123", "passw0rd", "12345678", "123456789", "1234567890",
  "123123123", "11111111", "00000000", "87654321", "qwertyui", "qwerty123", "qwertyuiop", "1q2w3e4r",
  "iloveyou", "letmein1", "welcome1", "welcome123", "admin123", "administrator", "abcd1234", "abc12345",
  "monkey123", "dragon123", "football", "baseball", "sunshine", "princess", "trustno1", "changeme",
  "deepsea", "shipwreck", "openwrecks",
]);

// ctx: { username, email } so a password can't just be the username or the email.
// Returns { value } or { error }. No composition rules (they push people to "Password1!"); length + not-obvious instead.
export function validatePassword(raw, ctx = {}) {
  if (typeof raw !== "string" || !raw) return { error: "Password is required" };
  if (raw.length < PASSWORD_MIN) return { error: `Password must be at least ${PASSWORD_MIN} characters` };
  if (raw.length > PASSWORD_MAX) return { error: `Password must be ${PASSWORD_MAX} characters or fewer` };
  if (!raw.trim()) return { error: "Password can't be only spaces" };
  const lower = raw.toLowerCase();
  const emailLocal = String(ctx.email || "").toLowerCase().split("@")[0];
  if (
    COMMON_PASSWORDS.has(lower) ||
    /^(.)\1+$/.test(raw) ||
    (ctx.username && lower === String(ctx.username).trim().toLowerCase()) ||
    (ctx.email && (lower === String(ctx.email).trim().toLowerCase() || (emailLocal.length >= 4 && lower === emailLocal)))
  ) {
    return { error: "That password is too easy to guess. Please choose a different one" };
  }
  return { value: raw };
}

/* ---------- free text (name, country) ---------- */

// control characters, zero-width characters, and the bidi overrides used to make text display backwards
// eslint-disable-next-line no-control-regex
const INVISIBLE = /[\u0000-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;

export function cleanText(raw, { label, min = 1, max }) {
  if (typeof raw !== "string") return { error: `${label} is required` };
  const value = raw.normalize("NFC").replace(INVISIBLE, "").replace(/\s+/g, " ").trim();
  if (value.length < min) return { error: min > 1 ? `${label} must be at least ${min} characters` : `${label} is required` };
  if (value.length > max) return { error: `${label} must be ${max} characters or fewer` };
  return { value };
}

/* ---------- the whole sign-up form ---------- */

// Returns { fields: { username, email, password, name, country } } or { error }.
export function validateSignup(data) {
  const d = data && typeof data === "object" ? data : {};
  const username = validateUsername(d.username);
  if (username.error) return { error: username.error };
  const email = validateEmail(d.email);
  if (email.error) return { error: email.error };
  const password = validatePassword(d.password, { username: username.value, email: email.value });
  if (password.error) return { error: password.error };
  const name = cleanText(d.name, { label: "Name", max: NAME_MAX });
  if (name.error) return { error: name.error };
  const country = cleanText(d.country, { label: "Country", min: COUNTRY_MIN, max: COUNTRY_MAX });
  if (country.error) return { error: country.error };
  return {
    fields: {
      username: username.value,
      email: email.value,
      password: password.value,
      name: name.value,
      country: country.value,
    },
  };
}
